import React, { Fragment, useState } from "react"
import { ButtonPrimaryOutlined } from "./Buttons"


// --- French BIP39 wordlist (2048 words, already bundled via bsv-mnemonic) ---
// Used for the passphrase generator so French-speaking users get words they
// can actually read and transcribe. ~55 bits of entropy for 5 words — strong
// enough combined with Argon2id (which makes each offline guess expensive).
import Mnemonic from "bsv-mnemonic"
const FRENCH_WORDS = Mnemonic.Words.FRENCH


// --- random helpers ---

function randomInt(max) {
	const arr = new Uint32Array(1)
	crypto.getRandomValues(arr)
	return arr[0] % max
}

function pickRandomWord() {
	return FRENCH_WORDS[randomInt(FRENCH_WORDS.length)]
}

// Full-charset random string — excludes confusable chars (0, O, l, 1, I).
const FULL_CHARSET = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!@#$%^&*"

function generateFullCharString(length = 18) {
	let result = ""
	for (let i = 0; i < length; i++) {
		result += FULL_CHARSET[randomInt(FULL_CHARSET.length)]
	}
	return result
}

function generatePassphrase(wordCount = 5) {
	const words = []
	for (let i = 0; i < wordCount; i++) {
		words.push(pickRandomWord())
	}
	return words.join("-")
}

function copyToClipboard(text) {
	if (navigator.clipboard && navigator.clipboard.writeText) {
		navigator.clipboard.writeText(text)
	} else {
		const textarea = document.createElement("textarea")
		textarea.value = text
		textarea.style.position = "fixed"
		textarea.style.opacity = "0"
		document.body.appendChild(textarea)
		textarea.select()
		document.execCommand("copy")
		document.body.removeChild(textarea)
	}
}


// Dark-theme colors matching the app (from appConfig.theme)
const COLORS = {
	background: "rgb(28, 43, 85)",       // backgroundColorLighter1
	border: "rgb(48, 63, 105)",         // backgroundColorLighter2
	textPrimary: "white",
	textSecondary: "silver",
	codeBackground: "rgb(3, 5, 41)",     // backgroundColorDarker1
}

/**
 * PasswordGenerator — a "Generate strong password" button that opens a
 * chooser with two benefit-oriented options:
 *   1. "I have a password manager" → full-charset random string (18 chars)
 *   2. "I want an easy-to-remember password" → 5 French words
 *
 * Generated passwords are shown unmasked with a copy button.
 * On pick, calls onGenerate(password) so the parent can fill the field.
 *
 * Props:
 *   - onGenerate: (password: string) => void
 *   - style: optional style for the outer container
 */
const PasswordGenerator = ({ onGenerate, style }) => {
	const [showChooser, setShowChooser] = useState(false)
	const [generated, setGenerated] = useState(null) // { password, type }
	const [copied, setCopied] = useState(false)

	const handleGenerate = (type) => {
		let password
		if (type === "random") {
			password = generateFullCharString(18)
		} else {
			password = generatePassphrase(5)
		}
		setGenerated({ password, type })
		setCopied(false)
	}

	const handleUse = () => {
		if (generated && onGenerate) {
			onGenerate(generated.password)
			setShowChooser(false)
			setGenerated(null)
			setCopied(false)
		}
	}

	const handleCopy = () => {
		if (generated) {
			copyToClipboard(generated.password)
			setCopied(true)
			setTimeout(() => setCopied(false), 1500)
		}
	}

	const handleBack = () => {
		setGenerated(null)
		setCopied(false)
	}

	const handleClose = () => {
		setShowChooser(false)
		setGenerated(null)
		setCopied(false)
	}

	const btnStyle = { borderRadius: "10em", padding: "4px 12px", fontSize: "13px" }

	return (
		<div style={{ marginTop: "8px", ...style }}>
			{!showChooser ? (
				<ButtonPrimaryOutlined
					onClick={() => setShowChooser(true)}
					style={{ borderRadius: "10em", padding: "4px 14px", fontSize: "13px" }}
				>
					<i className="fas fa-magic-wand-sparkles" /> &nbsp; Generate strong password
				</ButtonPrimaryOutlined>
			) : (
				<div style={{
					padding: "12px",
					textAlign: "left",
					backgroundColor: COLORS.background,
					border: `1px solid ${COLORS.border}`,
					borderRadius: "8px",
					color: COLORS.textPrimary,
				}}>
					{!generated ? (
						<Fragment>
							<b>Choose a style:</b>
							<br /><br />
							<div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
								<ButtonPrimaryOutlined
									onClick={() => handleGenerate("random")}
									style={{ borderRadius: "10em", padding: "6px 16px" }}
								>
									<i className="fas fa-key" /> &nbsp; I have a password manager
								</ButtonPrimaryOutlined>
								<ButtonPrimaryOutlined
									onClick={() => handleGenerate("passphrase")}
									style={{ borderRadius: "10em", padding: "6px 16px" }}
								>
									<i className="fas fa-book" /> &nbsp; I want an easy-to-remember password
								</ButtonPrimaryOutlined>
							</div>
							<br />
							<div style={{ textAlign: "right" }}>
								<ButtonPrimaryOutlined onClick={handleClose} style={btnStyle}>
									Cancel
								</ButtonPrimaryOutlined>
							</div>
						</Fragment>
					) : (
						<Fragment>
							<b>{generated.type === "random" ? "Random password" : "5-word passphrase"}</b>
							<br /><br />
							<div style={{
								fontFamily: "monospace",
								fontSize: "15px",
								wordBreak: "break-all",
								backgroundColor: COLORS.codeBackground,
								padding: "8px 10px",
								borderRadius: "5px",
								color: COLORS.textPrimary,
								border: `1px solid ${COLORS.border}`,
							}}>
								{generated.password}
							</div>
							<br />
							<div style={{ display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "center" }}>
								<ButtonPrimaryOutlined onClick={handleCopy} style={btnStyle}>
									{copied
										? <Fragment><i className="fas fa-check" /> Copied</Fragment>
										: <Fragment><i className="fas fa-copy" /> Copy</Fragment>}
								</ButtonPrimaryOutlined>
								<ButtonPrimaryOutlined
									onClick={() => handleGenerate(generated.type)}
									style={btnStyle}
								>
									<i className="fas fa-sync" /> Regenerate
								</ButtonPrimaryOutlined>
								<ButtonPrimaryOutlined onClick={handleUse} style={btnStyle}>
									<i className="fas fa-check" /> Use this
								</ButtonPrimaryOutlined>
								<ButtonPrimaryOutlined onClick={handleBack} style={btnStyle}>
									Back
								</ButtonPrimaryOutlined>
							</div>
						</Fragment>
					)}
				</div>
			)}
		</div>
	)
}

export default PasswordGenerator